import { useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function getDate(timestamp) {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return null;
  }

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getDayTimestamp(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

function formatDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).padStart(4, '0');

  return `${month}/${day}/${year}`;
}

export default function DatePicker({
  value = null,
  onChange,
  disabled = false,
  className = '',
}) {
  const selectedDate = getDate(value);
  const selectedTimestamp = selectedDate
    ? getDayTimestamp(selectedDate)
    : null;

  const todayTimestamp = getDayTimestamp(new Date());

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const date = getDate(value) || new Date();

    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  function selectToday() {
    const today = new Date();

    setVisibleMonth(
      new Date(today.getFullYear(), today.getMonth(), 1),
    );

    onChange?.(getDayTimestamp(today));
  }

  const iconButtonClass = `
    flex h-10 w-10 shrink-0 items-center justify-center rounded-lg
    text-white/60 transition-colors
    enabled:hover:bg-white/10 enabled:hover:text-white
    focus-visible:outline-none focus-visible:ring-2
    focus-visible:ring-[#f9a928]
    disabled:cursor-not-allowed
  `;

  return (
    <div
      role="group"
      aria-label="Date picker"
      aria-disabled={disabled}
      className={`
        w-full min-w-0 rounded-2xl border border-white/10
        bg-neutral-900 p-3 text-white sm:p-4
        ${disabled ? 'opacity-40' : ''}
        ${className}
      `}
    >
      {/* Selected date */}
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f9a928]/10 text-[#f9a928]">
          <CalendarDays size={20} />
        </div>

        <div
          aria-live="polite"
          className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5"
        >
          <span className="text-white/45">
            {selectedDate ? 'Selected date' : 'Choose a date'}
          </span>

          <span className="font-semibold tracking-wide">
            {selectedDate ? formatDate(selectedDate) : 'MM/DD/YYYY'}
          </span>
        </div>
      </div>

      {/* Month navigation */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <div
          aria-live="polite"
          className="min-w-0 font-semibold tracking-tight"
        >
          {visibleMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
          })}
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            disabled={disabled}
            aria-label="Previous month"
            className={iconButtonClass}
            onClick={() =>
              setVisibleMonth(new Date(year, month - 1, 1))
            }
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            disabled={disabled}
            aria-label="Next month"
            className={iconButtonClass}
            onClick={() =>
              setVisibleMonth(new Date(year, month + 1, 1))
            }
          >
            <ChevronRight size={22} />
          </button>
        </div>
      </div>

      {/* Weekday headings */}
      <div className="mb-1 grid grid-cols-7 gap-1">
        {weekdays.map((day) => (
          <div
            key={day}
            aria-hidden="true"
            className="py-1 text-center font-medium text-white/35"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Always render 42 cells to keep six consistent calendar rows. */}
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 42 }, (_, index) => {
          const day = index - firstWeekday + 1;

          if (day < 1 || day > daysInMonth) {
            return (
              <div
                key={`empty-${index}`}
                aria-hidden="true"
                className="h-9"
              />
            );
          }

          const date = new Date(year, month, day);
          const timestamp = date.getTime();

          const isSelected = timestamp === selectedTimestamp;
          const isToday = timestamp === todayTimestamp;

          return (
            <button
              key={index}
              type="button"
              disabled={disabled}
              aria-label={formatDate(date)}
              aria-pressed={isSelected}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => onChange?.(timestamp)}
              className={`
                relative flex h-9 min-w-0 items-center justify-center
                rounded-lg font-medium
                transition-[background-color,color,box-shadow] duration-150
                focus-visible:z-10 focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-[#f9a928]
                focus-visible:ring-offset-2
                focus-visible:ring-offset-neutral-900
                disabled:cursor-not-allowed
                ${
                  isSelected
                    ? 'bg-[#f9a928] font-semibold text-neutral-950 shadow-[0_2px_10px_rgba(249,169,40,0.2)]'
                    : isToday
                      ? 'bg-[#f9a928]/10 text-[#f9a928] enabled:hover:bg-[#f9a928]/20'
                      : 'text-white/75 enabled:hover:bg-white/10 enabled:hover:text-white'
                }
              `}
            >
              {day}

              {isToday && (
                <span
                  className={`
                    absolute bottom-1 h-1 w-1 rounded-full
                    ${isSelected ? 'bg-neutral-950' : 'bg-[#f9a928]'}
                  `}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex justify-end border-t border-white/10 pt-2">
        <button
          type="button"
          disabled={disabled}
          onClick={selectToday}
          className="
            rounded-lg px-3 py-1.5 font-medium text-[#f9a928]
            transition-colors enabled:hover:bg-[#f9a928]/10
            focus-visible:outline-none focus-visible:ring-2
            focus-visible:ring-[#f9a928]
            disabled:cursor-not-allowed
          "
        >
          Today
        </button>
      </div>
    </div>
  );
}
